import type { MessagePayload } from 'firebase/messaging'
import {
	registerDeviceToken,
	unregisterDeviceToken,
} from '@/api/services/notifications'
import { getFirebaseApp, isFirebaseConfigured } from '@/lib/firebase'

// firebase/messaging só entra no bundle quando o push é usado
const loadMessaging = () => import('firebase/messaging')

export function isPushSupported(): boolean {
	return (
		isFirebaseConfigured &&
		typeof window !== 'undefined' &&
		'Notification' in window &&
		'serviceWorker' in navigator &&
		'PushManager' in window
	)
}

export function getPushPermission(): NotificationPermission | 'unsupported' {
	return isPushSupported() ? Notification.permission : 'unsupported'
}

async function getCurrentToken(): Promise<string | null> {
	const { getMessaging, getToken, isSupported } = await loadMessaging()
	if (!(await isSupported())) return null

	// Reaproveita o service worker do PWA para não haver dois workers no escopo "/".
	// "ready" nunca resolve se nenhum worker foi registrado, então limitamos a espera.
	const serviceWorkerRegistration = await Promise.race([
		navigator.serviceWorker.ready,
		new Promise<never>((_, reject) =>
			setTimeout(
				() =>
					reject(
						new Error(
							'Service worker não registrado. Em dev ele só é servido com VITE_FIREBASE_API_KEY definida; reinicie o npm run dev.',
						),
					),
				8000,
			),
		),
	])
	console.info('[push] service worker pronto', serviceWorkerRegistration.scope)
	const token = await getToken(getMessaging(getFirebaseApp()), {
		vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
		serviceWorkerRegistration,
	})
	return token || null
}

/** Registra (upsert) o token do dispositivo no backend, se a permissão já foi concedida. */
export async function syncPushToken(): Promise<void> {
	if (getPushPermission() !== 'granted') return
	const token = await getCurrentToken()
	if (!token)
		throw new Error('O Firebase não retornou um token (push não suportado?)')
	console.info('[push] token obtido, registrando no backend')
	await registerDeviceToken(token)
	console.info('[push] token registrado no backend')
}

/** Pede permissão ao usuário (precisa vir de um clique) e registra o token. */
export async function enablePush(): Promise<
	NotificationPermission | 'unsupported'
> {
	if (!isPushSupported()) return 'unsupported'
	const permission = await Notification.requestPermission()
	if (permission === 'granted') await syncPushToken()
	return permission
}

/** Remove o token do backend e do FCM. Best-effort: nunca bloqueia o logout. */
export async function disablePush(): Promise<void> {
	try {
		if (getPushPermission() !== 'granted') return
		const token = await getCurrentToken()
		if (!token) return
		await unregisterDeviceToken(token)
		const { deleteToken, getMessaging } = await loadMessaging()
		await deleteToken(getMessaging(getFirebaseApp()))
	} catch {
		// sem rede ou sessão já expirada: o backend limpa tokens inválidos no próximo envio
	}
}

/** Escuta pushes com a aba em foreground. Retorna a função de cancelamento. */
export async function listenForegroundPush(
	handler: (payload: MessagePayload) => void,
): Promise<() => void> {
	const { getMessaging, isSupported, onMessage } = await loadMessaging()
	if (!(await isSupported())) return () => {}
	return onMessage(getMessaging(getFirebaseApp()), handler)
}
